import {
  Injectable,
  inject,
  signal,
  computed,
  Signal,
  WritableSignal,
} from '@angular/core';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';
import { Auth } from '../core/firebase/auth';
import { SchoolAuthorityPermissionsApiService } from './school-authority-permissions-api.service';
import type {
  AuthorityRoleEnum,
  MySchoolPermissionsRes,
} from '../types/rest-api';

const CAN_EDIT_ROLES = new Set<AuthorityRoleEnum>([
  'OWNER',
  'ADMIN',
  'MANAGER',
  'PROFILE_EDITOR',
]);
const CAN_MANAGE_TEAM_ROLES = new Set<AuthorityRoleEnum>(['OWNER', 'ADMIN']);
const CAN_VIEW_PROFILE_ROLES = new Set<AuthorityRoleEnum>([
  'OWNER',
  'ADMIN',
  'MANAGER',
  'VIEWER',
  'PROFILE_EDITOR',
]);
const CAN_VIEW_STRUCTURE_ROLES = new Set<AuthorityRoleEnum>([
  'OWNER',
  'ADMIN',
  'MANAGER',
  'VIEWER',
]);
const CAN_MANAGE_JOBS_ROLES = new Set<AuthorityRoleEnum>([
  'OWNER',
  'ADMIN',
  'MANAGER',
  'HR',
]);
const CAN_VIEW_JOBS_ROLES = new Set<AuthorityRoleEnum>([
  'OWNER',
  'ADMIN',
  'MANAGER',
  'HR',
  'VIEWER',
]);

const CAN_MANAGE_STRUCTURE_ROLES = new Set<AuthorityRoleEnum>([
  'OWNER',
  'ADMIN',
  'MANAGER',
]);

/**
 * A Schulgemeinde role as the capability flags `GET /schools/{id}/my-role` returns
 * for a unit, so callers can treat both contexts alike. `null` grants nothing.
 */
export function authorityCapabilities(
  role: AuthorityRoleEnum | null
): MySchoolPermissionsRes {
  const has = (roles: Set<AuthorityRoleEnum>) =>
    role !== null && roles.has(role);
  return {
    can_view: role !== null,
    can_edit_profile: has(CAN_EDIT_ROLES),
    can_manage_structure: has(CAN_MANAGE_STRUCTURE_ROLES),
    can_view_jobs: has(CAN_VIEW_JOBS_ROLES),
    can_view_structure: has(CAN_VIEW_STRUCTURE_ROLES),
    can_view_profile: has(CAN_VIEW_PROFILE_ROLES),
    can_manage_jobs: has(CAN_MANAGE_JOBS_ROLES),
    can_manage_team: has(CAN_MANAGE_TEAM_ROLES),
  };
}

@Injectable({ providedIn: 'root' })
export class AuthorityPermissionService {
  private readonly api = inject(SchoolAuthorityPermissionsApiService);
  private readonly auth = inject(Auth);

  private readonly cache = new Map<
    string,
    WritableSignal<AuthorityRoleEnum | null>
  >();
  /**
   * Whether the `my-role` request for an authority has come back — successfully *or* not.
   * `cache` alone cannot express this: `null` there means "loading", "no access" and
   * "request failed" alike, so a caller that defers rendering until the role is known
   * would hang forever on a failed fetch.
   */
  private readonly settledCache = new Map<string, WritableSignal<boolean>>();
  /**
   * Authority ids whose role fetch (or seed) has already been initiated. Tracked
   * separately from {@link cache}: every capability getter lazily creates a
   * `signal(null)` cache entry, so guarding `loadRole` on `cache.has` would let a
   * mere capability read permanently suppress the fetch (role stuck at `null`).
   */
  private readonly loadInitiated = new Set<string>();

  private getOrCreate(
    authorityId: string
  ): WritableSignal<AuthorityRoleEnum | null> {
    if (!this.cache.has(authorityId)) {
      this.cache.set(authorityId, signal(null));
    }
    return this.cache.get(authorityId)!;
  }

  private getOrCreateSettled(authorityId: string): WritableSignal<boolean> {
    if (!this.settledCache.has(authorityId)) {
      this.settledCache.set(authorityId, signal(false));
    }
    return this.settledCache.get(authorityId)!;
  }

  /** Fetch the caller's role for `authorityId` if not already fetched/seeded. */
  loadRole(authorityId: string): void {
    if (this.loadInitiated.has(authorityId)) return;
    this.loadInitiated.add(authorityId);
    const s = this.getOrCreate(authorityId);
    const settled = this.getOrCreateSettled(authorityId);
    this.whenSignedIn(() => this.api.getMyRole(authorityId)).subscribe({
      next: res => {
        s.set(res?.role ?? null);
        settled.set(true);
        if (res === null) this.loadInitiated.delete(authorityId);
      },
      error: () => {
        s.set(null);
        settled.set(true);
        // Allow a retry on the next trigger after a failed fetch.
        this.loadInitiated.delete(authorityId);
      },
    });
  }

  /** Guests have no role anywhere: answer `null` without a request that only 401s. */
  private whenSignedIn<T>(request: () => Observable<T>): Observable<T | null> {
    if (this.auth.currentUser) return request();
    return from(this.auth.authStateReady()).pipe(
      switchMap(() => (this.auth.currentUser ? request() : of(null)))
    );
  }

  /** Populate the cache from already-fetched data (no HTTP call). */
  seedRole(authorityId: string, role: AuthorityRoleEnum | null): void {
    this.getOrCreate(authorityId).set(role);
    this.getOrCreateSettled(authorityId).set(true);
    // A seeded role is authoritative — don't let a later loadRole re-fetch over it.
    this.loadInitiated.add(authorityId);
  }

  /**
   * The caller's role once known, for route guards that cannot wait on a signal.
   * `null` means not a member, `undefined` that the request failed.
   */
  ensureRole(
    authorityId: string
  ): Observable<AuthorityRoleEnum | null | undefined> {
    // A refusal is asked again: the user may have been invited since.
    const cached = this.getOrCreate(authorityId)();
    if (cached) return of(cached);
    return this.api.getMyRole(authorityId).pipe(
      map(res => {
        this.seedRole(authorityId, res.role);
        return res.role;
      }),
      catchError(() => of(undefined))
    );
  }

  /** Signal with the caller's current role (`null` = loading or no access). */
  getRole(authorityId: string): Signal<AuthorityRoleEnum | null> {
    return this.getOrCreate(authorityId).asReadonly();
  }

  /** `true` once the role request for `authorityId` resolved, failed, or was seeded. */
  settled(authorityId: string): Signal<boolean> {
    return this.getOrCreateSettled(authorityId).asReadonly();
  }

  /** `true` for OWNER, ADMIN, MANAGER, PROFILE_EDITOR — allows editing postings and profiles. */
  canEdit(authorityId: string): Signal<boolean> {
    const role = this.getOrCreate(authorityId);
    return computed(() => {
      const r = role();
      return r !== null && CAN_EDIT_ROLES.has(r);
    });
  }

  /** `true` for OWNER and ADMIN only — allows adding/removing team members. */
  canManageTeam(authorityId: string): Signal<boolean> {
    const role = this.getOrCreate(authorityId);
    return computed(() => {
      const r = role();
      return r !== null && CAN_MANAGE_TEAM_ROLES.has(r);
    });
  }

  /** `true` for all roles except HR — can view the Profil & Auftritt section. */
  canViewProfile(authorityId: string): Signal<boolean> {
    const role = this.getOrCreate(authorityId);
    return computed(() => {
      const r = role();
      return r !== null && CAN_VIEW_PROFILE_ROLES.has(r);
    });
  }

  /** `true` for OWNER, ADMIN, MANAGER, VIEWER — can view Strukturverwaltung. */
  canViewStructure(authorityId: string): Signal<boolean> {
    const role = this.getOrCreate(authorityId);
    return computed(() => {
      const r = role();
      return r !== null && CAN_VIEW_STRUCTURE_ROLES.has(r);
    });
  }

  /** `true` for OWNER, ADMIN, MANAGER, HR, VIEWER — can view Inserate & Bewerbungen. */
  canViewJobs(authorityId: string): Signal<boolean> {
    const role = this.getOrCreate(authorityId);
    return computed(() => {
      const r = role();
      return r !== null && CAN_VIEW_JOBS_ROLES.has(r);
    });
  }

  /** `true` for OWNER, ADMIN, MANAGER, HR — can edit postings and message applicants. */
  canManageJobs(authorityId: string): Signal<boolean> {
    const role = this.getOrCreate(authorityId);
    return computed(() => {
      const r = role();
      return r !== null && CAN_MANAGE_JOBS_ROLES.has(r);
    });
  }

  /** Clear cached role for one authority (or all if `authorityId` is omitted). */
  clearCache(authorityId?: string): void {
    if (authorityId !== undefined) {
      this.cache.delete(authorityId);
      this.settledCache.delete(authorityId);
      this.loadInitiated.delete(authorityId);
    } else {
      this.cache.clear();
      this.settledCache.clear();
      this.loadInitiated.clear();
    }
  }
}
